import type { PaginationState } from '@tanstack/react-table';
import { FileText } from 'lucide-react';
import * as React from 'react';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@payment-plan/ui';
import { CierreFeedback, DescartarBorradorButton, MENSAJE_DESCARTADA } from './components/CierreSeparacion';
import { SearchBar } from './components/SearchBar';
import { SeparationsTable } from './components/SeparationsTable';
import { ESTADO_BORRADOR } from './estados';
import { etiquetaUnidad } from './formato';
import { useMySeparations } from './use-consultas';
import { useAuth } from '../../auth/auth-context';
import { can } from '../../lib/permissions';
import type { ConsultaPropiasParams, SeparacionEstado } from '../../types/consultas';

const PAGE_SIZE = 20;

/** «Mis separaciones» del asesor (HU-C08): solo las propias; el alcance lo decide el servidor. */
export function MySeparationsPage(): React.JSX.Element {
  const [q, setQ] = React.useState('');
  const [estado, setEstado] = React.useState<SeparacionEstado | ''>('');
  const [paginacion, setPaginacion] = React.useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });

  const params = React.useMemo<ConsultaPropiasParams>(
    () => ({
      ...(q ? { q } : {}),
      ...(estado ? { estado } : {}),
      page: paginacion.pageIndex + 1,
      pageSize: paginacion.pageSize,
    }),
    [q, estado, paginacion],
  );
  const { data, isLoading, isError, refetch, isFetching } = useMySeparations(params);
  const conFiltros = Boolean(q || estado);
  const { user } = useAuth();
  // feat-67: el asesor descarta sus borradores abandonados para liberar la unidad.
  const puedeDescartar = can(user?.permissions, 'separacion.descartar');
  const [exito, setExito] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const reiniciarPagina = () => setPaginacion((p) => ({ ...p, pageIndex: 0 }));

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader
        eyebrow="Asesor comercial"
        title="Mis separaciones"
        description="Consulta tus separaciones, su plan de pagos y vuelve a descargar el PDF."
      />

      <CierreFeedback exito={exito} error={error} />

      <SearchBar
        onBuscar={(termino) => {
          setQ(termino);
          reiniciarPagina();
        }}
        estado={estado}
        onEstado={(valor) => {
          setEstado(valor);
          reiniciarPagina();
        }}
      />

      {isLoading ? (
        <LoadingState message="Cargando tus separaciones" />
      ) : isError ? (
        <ErrorState
          message="No fue posible cargar tus separaciones. Intenta de nuevo en un momento."
          onRetry={() => void refetch()}
        />
      ) : (data?.data.length ?? 0) === 0 ? (
        <EmptyState
          icon={<FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
          title={conFiltros ? 'Sin resultados' : 'Aún no tienes separaciones'}
          description={
            conFiltros
              ? 'Ninguna separación coincide con la búsqueda o el estado elegido.'
              : 'Cuando registres una separación aparecerá aquí.'
          }
        />
      ) : (
        <SeparationsTable
          filas={data?.data ?? []}
          total={data?.meta.total ?? 0}
          paginacion={paginacion}
          onPaginacion={setPaginacion}
          rutaDetalle="/separations"
          cargando={isFetching && !data}
          accionesFila={(fila) =>
            puedeDescartar && fila.estado === ESTADO_BORRADOR ? (
              <DescartarBorradorButton
                separacionId={fila.id}
                etiqueta={`Descartar borrador de ${etiquetaUnidad(fila.unidad)}`}
                onDescartada={() => {
                  setError(null);
                  setExito(MENSAJE_DESCARTADA);
                }}
                onError={(mensaje) => {
                  setExito(null);
                  setError(mensaje);
                }}
              />
            ) : null
          }
        />
      )}
    </div>
  );
}
